import { useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import {
    useAPIQuery,
    useGlobalAPIQueryContext,
} from "@dev2_techwalnut/tw-api-query-hook";
import moment from "moment";
import Swal from "sweetalert2";
import secureLocalStorage from "react-secure-storage";

const useDailyPlanCalendar = (props) => {
    const [woEvents, setWoEvents] = useState([]);
    const [backupEvents, setBackupEvents] = useState([]);
    const [currentSlot, setCurrentSlot] = useState(null);
    const [currentWO, setCurrentWO] = useState(null);
    const [currentDate, setCurrentDate] = useState(null);
    const userID = secureLocalStorage.getItem("userID");
    const { invalidateApi } = useGlobalAPIQueryContext();

    const { useFetch: useDailyPlanList } = useAPIQuery({
        key: "work-order",
        endpoint: "/api/work-order/",
    });
    const {
        data: dailyPlanData,
        isLoading: loadingWorkOrdersData,
        isError: errorLoadingWorkOrders,
        refetch: refetchDailyPlan,
        isFetching: fetchingDailyPlan,
    } = useDailyPlanList({
        params: {
            action: "daily_plan",
            machine_id: props.machine_id,
        },
        abortOnNewRequest: true,
        enabled: props.machine_id === props.activeAccordion,
    });

    const { useFetch: useFetchMachineInfo, useCreate: useCreateDailyPlans } =
        useAPIQuery({
            key: `machine-info-${props?.machine_id}`,
            endpoint: "/api/daily-plan-machine/",
        });
    const { useDelete: useDeleteDailyPlan } = useAPIQuery({
        key: "daily-plan",
        endpoint: "/api/daily-plan/",
    });
    const {
        data: machineData,
        isLoading: loadingMachineInfoData,
        isError: errorLoadingMachineInfo,
        isFetching: fetchingMachineData,
        refetch: refetchMachineData,
    } = useFetchMachineInfo({
        params: {
            machine_id: props.machine_id,
            date: moment(currentDate ?? new Date()).format("yyyy-MM"),
        },
        staleTime: 500000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: 3,
        keepPlaceholder: true,
        enabled: props.machine_id === props.activeAccordion,
    });
    const { mutate: deleteDailyPlan, isPending: deletingDailyPlan } =
        useDeleteDailyPlan({
            onSuccess: (data) => {
                Swal.fire({
                    icon: "success",
                    title: "Success!",
                    text: `Deleted Successfully`,
                }).then((result) => {
                    refetchMachineData();
                    refetchDailyPlan();
                    invalidateApi({ key: "work-order" });
                    setBackupEvents(woEvents);
                });
            },
            onError: (error) => {
                Swal.fire({
                    icon: "error",
                    title: "Error!",
                    text: `Unable to delete the daily plan`,
                }).then((result) => {
                    refetchMachineData();
                    setWoEvents(backupEvents);
                });
            },
        });
    const { mutate: createDailyPlan, isPending: creatingDailyPlans } =
        useCreateDailyPlans({
            onSuccess: (data) => {
                Swal.fire({
                    title: "Success!",
                    text: data?.message,
                    icon: "success",
                }).then((result) => {
                    refetchDailyPlan();
                    invalidateApi({
                        key: ["work-order", "daily-plan", "api/generic"],
                    });
                    setBackupEvents(woEvents);
                });
            },
            onError: (error) => {
                Swal.fire({
                    title: "Error!",
                    text: error?.message,
                    icon: "error",
                }).then((result) => {
                    setWoEvents(backupEvents);
                });
            },
        });
    const machine = useMemo(() => {
        if (machineData) {
            const res = machineData?.data?.[0];
            const data = [
                ...res.daily_plan.map((os) => ({
                    id: os.id,
                    // Planned target, which survives closure; os.target is
                    // rewritten to the actual produced quantity when a plan
                    // closes short. Fallback covers plans predating the field.
                    title: `${os.wo.work_order_code} - ${
                        os.target_original ?? os.target
                    }`,
                    target_original: os.target_original ?? os.target,
                    wo: os.wo.id,
                    start: new Date(os.date),
                    end: new Date(os.date),
                    amount: os.target_cycles,
                    bo: os?.bo,
                    daily_plan_code: os?.daily_plan_code,
                    mould_size: os?.mould_size ?? 1,
                })),
            ];
            setWoEvents(data);
            setBackupEvents(data);
            return {
                ...res,
            };
        }
    }, [machineData]);

    const workOrders = useMemo(() => {
        if (dailyPlanData) {
            return dailyPlanData?.data;
        }
    }, [dailyPlanData]);

    const handleSelectSlot = (slotInfo) => {
        if (!machine) return;
        if (
            new Date(slotInfo.slots[0]).setHours(0, 0, 0, 0) <
            new Date().setHours(0, 0, 0, 0)
        )
            return;

        if (
            !machine?.shifts?.[moment(slotInfo.slots[0]).format("YYYY-MM-DD")]
                ?.value
        ) {
            if (
                machine?.shifts?.[
                    moment(slotInfo.slots[0]).format("YYYY-MM-DD")
                ]?.shift === "Idle"
            ) {
                Swal.fire({
                    icon: "error",
                    title: "Error!",
                    text: "Cannot assign work order on an idle shift",
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error!",
                    text: "No shift associated for the selected date",
                });
            }
            return;
        }

        // find all the work orders with same date as selected date
        const slots = woEvents.filter((os) => {
            return (
                new Date(os.start).setHours(0, 0, 0, 0) ===
                new Date(slotInfo.slots[0]).setHours(0, 0, 0, 0)
            );
        });

        // check if the total amount of work orders is greater than machine capacity
        const totalByAmount = slots.reduce((sum, slot) => sum + slot.amount, 0);

        // if the total amount is greater than machine capacity, return
        if (
            totalByAmount >=
            machine?.capacity *
                machine?.shifts?.[
                    moment(slotInfo.slots[0]).format("YYYY-MM-DD")
                ]?.value
        ) {
            Swal.fire({
                icon: "error",
                title: "Error!",
                text: "Machine capacity is not available!",
            });
            return;
        }

        setCurrentSlot(slotInfo.slots[0]);

        // open modal
        const button = document.createElement("button");
        button.className = "d-none";
        button.id = "open-modal";
        button.setAttribute("data-bs-toggle", "modal");
        button.setAttribute(
            "data-bs-target",
            `#add-wo-modal-${props.machine_id}`
        );
        document.body.appendChild(button);
        button.click();
        button.remove();
    };

    const handleSelectEvent = (event) => {
        setCurrentWO(event);
        const button = document.createElement("button");
        button.className = "d-none";
        button.id = "open-edit-modal";
        button.setAttribute("data-bs-toggle", "modal");
        button.setAttribute(
            "data-bs-target",
            `#edit-wo-modal-${props.machine_id}`
        );
        document.body.appendChild(button);
        button.click();
        button.remove();
    };

    const validateSlot = (slot) => {
        if (!machine) return;
        const events = woEvents.filter(
            (event) =>
                new Date(event.start).setHours(0, 0, 0, 0) ===
                new Date(slot).setHours(0, 0, 0, 0)
        );
        const totalByAmount = events.reduce(
            (sum, event) => sum + Math.ceil(event.amount),
            0
        );
        if (
            !machine?.shifts?.[moment(slot).format("YYYY-MM-DD")]?.value ||
            totalByAmount >=
                machine.capacity *
                    machine?.shifts?.[moment(slot).format("YYYY-MM-DD")]?.value
        )
            return false;
        return {
            available:
                machine.capacity *
                    machine?.shifts?.[moment(slot).format("YYYY-MM-DD")]
                        ?.value -
                totalByAmount,
        };
    };

    const handleCreateDailyPlan = (data, work_order) => {
        const res = {
            build_order_id: work_order?.id,
            bulk_upload: data?.map((d) => ({
                id: null,
                date: moment(d?.start).format("yyyy-MM-DD"),
                qty_to_be_produced: d?.amount,
                wo_id: d?.wo,
                machine_id: props?.machine_id,
                item_id: work_order?.item?.id,
                build_order_id: work_order?.id,
                mould_size: d?.mould_size ?? 1,
            })),
            created_by: userID,
            updated_by: userID,
        };
        createDailyPlan({
            data: res,
        });
    };

    const handleUpdateDailyPlan = (data, additionalWOs, work_order) => {
        const res = {
            build_order_id: work_order?.id,
            bulk_upload: [
                ...additionalWOs?.map((wo) => ({
                    id: null,
                    date: moment(wo?.start).format("yyyy-MM-DD"),
                    qty_to_be_produced: wo?.amount,
                    wo_id: data?.wo,
                    machine_id: props?.machine_id,
                    item_id: work_order?.item?.id,
                    build_order_id: work_order?.id,
                    mould_size: data?.mould_size ?? 1,
                })),
                {
                    id: data?.id,
                    date: moment(data?.start).format("yyyy-MM-DD"),
                    qty_to_be_produced: data?.amount,
                    wo_id: data?.wo,
                    machine_id: props?.machine_id,
                    item_id: work_order?.item?.id,
                    build_order_id: work_order?.id,
                    mould_size: data?.mould_size ?? 1,
                },
            ],
            created_by: userID,
            updated_by: userID,
        };
        // console.log(reason);
        createDailyPlan({
            data: res,
        });
    };

    const addNewDailyPlans = (wo, fromDate, toAssign) => {
        const workOrder = workOrders?.find((w) => w?.id === wo?.id);
        if (!workOrder) return;

        let dateTemp = new Date(fromDate);
        dateTemp.setHours(0, 0, 0, 0);

        const beforeDate = new Date(workOrder?.work_order?.target_date);
        beforeDate.setHours(0, 0, 0, 0);

        let newEvents = [];
        let assignedAmount = toAssign;

        while (dateTemp <= beforeDate && assignedAmount > 0) {
            const availability = validateSlot(dateTemp);
            if (availability) {
                if (assignedAmount - availability.available > 0) {
                    const newProduce = availability.available;
                    newEvents.push({
                        id: uuid(),
                        title: `${workOrder?.work_order?.work_order_code} - ${
                            newProduce * (wo?.item?.mould_size ?? 1)
                        }`,
                        target_original:
                            newProduce * (wo?.item?.mould_size ?? 1),
                        wo: wo?.id,
                        start: new Date(dateTemp),
                        end: new Date(dateTemp),
                        amount: newProduce,
                        mould_size: wo?.item?.mould_size ?? 1,
                    });
                    assignedAmount -= availability.available;
                } else {
                    const newProduce = assignedAmount;
                    newEvents.push({
                        id: uuid(),
                        title: `${workOrder?.work_order?.work_order_code} - ${
                            newProduce * (wo?.item?.mould_size ?? 1)
                        }`,
                        target_original:
                            newProduce * (wo?.item?.mould_size ?? 1),
                        wo: wo?.id,
                        start: new Date(dateTemp),
                        end: new Date(dateTemp),
                        amount: newProduce,
                        mould_size: wo?.item?.mould_size ?? 1,
                    });
                    assignedAmount = 0;
                }
            }
            const nextDate = new Date(dateTemp);
            nextDate.setDate(nextDate.getDate() + 1);
            dateTemp = nextDate;
        }
        return newEvents ?? [];
    };

    const handleWOSelect = (wo, mould_size) => {
        const workOrder = workOrders?.find((w) => w?.id === wo?.value);
        if (!workOrder) return;

        let dateTemp = new Date(currentSlot);
        dateTemp.setHours(0, 0, 0, 0);

        const beforeDate = new Date(workOrder?.work_order?.target_date);
        beforeDate.setHours(0, 0, 0, 0);

        let newEvents = [];
        let assignedAmount = Math.ceil(
            (workOrder?.work_order_req_quantity -
                workOrder?.allotted_quantity_dailyplan) /
                mould_size
        );

        while (dateTemp <= beforeDate && assignedAmount > 0) {
            const availability = validateSlot(dateTemp);

            if (availability) {
                if (assignedAmount - availability.available > 0) {
                    const newProduce = availability.available;
                    newEvents.push({
                        id: uuid(),
                        title: `${workOrder?.work_order?.work_order_code} - ${
                            newProduce * (mould_size ?? 1)
                        }`,
                        target_original: newProduce * (mould_size ?? 1),
                        wo: wo?.work_order?.id,
                        start: new Date(dateTemp),
                        end: new Date(dateTemp),
                        amount: newProduce,
                        mould_size: mould_size ?? 1,
                    });
                    assignedAmount -= availability.available;
                } else {
                    const newProduce = assignedAmount;
                    newEvents.push({
                        id: uuid(),
                        title: `${workOrder?.work_order?.work_order_code} - ${
                            newProduce * (mould_size ?? 1)
                        }`,
                        target_original: newProduce * (mould_size ?? 1),
                        wo: wo?.work_order?.id,
                        start: new Date(dateTemp),
                        end: new Date(dateTemp),
                        amount: newProduce,
                        mould_size: mould_size ?? 1,
                    });
                    assignedAmount = 0;
                }
            }
            const nextDate = new Date(dateTemp);
            nextDate.setDate(nextDate.getDate() + 1);
            dateTemp = nextDate;
        }

        const totalAmount = newEvents.reduce(
            (sum, item) => sum + item.amount,
            0
        );

        if (totalAmount <= 0) {
            Swal.fire({
                title: "Error",
                text: `Invalid amount allocation, Amount: ${
                    totalAmount * (wo?.item?.mould_size ?? 1)
                }`,
                icon: "success",
            });
        }

        if (assignedAmount > 0) {
            Swal.fire({
                title: "Machine Capacity Issue",
                text: `You are allocating the quantity ${
                    workOrder?.work_order_req_quantity -
                    workOrder?.allotted_quantity_dailyplan
                }, but the machine capacity is not available to allocate all the amount until the target date i.e ${moment(
                    workOrder?.work_order?.target_date
                ).format(
                    "DD-MM-YYYY"
                )}. Please allocate the remaining amount to a different machine. Proceed with the given quantity ${
                    totalAmount * (mould_size ?? 1)
                }?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Proceed",
                cancelButtonText: "Cancel",
            }).then((result) => {
                if (result.isConfirmed) {
                    handleCreateDailyPlan(newEvents, wo);
                    setWoEvents((prev) => [...prev, ...newEvents]);
                    return;
                }
            });
        } else {
            Swal.fire({
                title: "Are you sure?",
                text: `Do you want to allocate ${
                    totalAmount * (mould_size ?? 1)
                }`,
                icon: "info",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Confirm",
            }).then((result) => {
                if (result.isConfirmed) {
                    handleCreateDailyPlan(newEvents, wo);
                    setWoEvents((prev) => [...prev, ...newEvents]);
                    return;
                }
            });
        }
    };

    const handleWOEdit = (date, quantity) => {
        const events = woEvents.filter((event) => {
            const eventDate = new Date(event.start).setHours(0, 0, 0, 0);
            const inputDate = new Date(date).setHours(0, 0, 0, 0);
            return eventDate === inputDate;
        });

        const workOrder = workOrders?.find((w) => w?.id === currentWO?.bo);

        const toAllot = events.reduce(
            (accumulator, current) => accumulator + current.amount,
            0
        );

        let additionalWOs = [];
        let remainingQuantity = 0;

        if (events.length === 1) {
            if (events.length > 0) {
                if (
                    new Date(currentWO?.start).setHours(0, 0, 0, 0) !==
                    new Date(date).setHours(0, 0, 0, 0)
                ) {
                    if (
                        machine?.capacity *
                            machine?.shifts?.[moment(date).format("YYYY-MM-DD")]
                                ?.value -
                            toAllot <=
                        0
                    ) {
                        Swal.fire({
                            icon: "error",
                            title: "Not allowed",
                            text: `Cannot assign daily plan on a date where capacity is not available`,
                        });
                        return;
                    }
                    const newDate = new Date(date);
                    newDate.setDate(newDate.getDate() + 1);

                    remainingQuantity =
                        quantity -
                        (machine?.capacity *
                            machine?.shifts?.[moment(date).format("YYYY-MM-DD")]
                                ?.value -
                            toAllot);

                    additionalWOs = addNewDailyPlans(
                        workOrder,
                        newDate,
                        remainingQuantity
                    );
                }
            }
        }

        if (workOrder?.work_order?.target_date) {
            const beforeDate = new Date(
                workOrder?.work_order?.target_date
            ).setHours(0, 0, 0, 0);
            const newDate = new Date(date).setHours(0, 0, 0, 0);
            if (beforeDate < newDate) {
                Swal.fire({
                    icon: "error",
                    title: "Not allowed",
                    text: `Cannot assign daily plan after target date i.e ${moment(
                        beforeDate
                    ).format("yyyy-MM-DD")}`,
                });
                return;
            }
        }

        if (
            new Date(date).setHours(0, 0, 0, 0) <
            new Date().setHours(0, 0, 0, 0)
        ) {
            Swal.fire({
                icon: "error",
                title: "Not allowed",
                text: `Cannot assign daily plan before current date i.e ${moment(
                    new Date()
                ).format("yyyy-MM-DD")}`,
            });
            return;
        }

        let updatedDP = null;

        const updatedEvents = woEvents.map((event) => {
            if (event.id === currentWO?.id) {
                if (quantity) {
                    const newQuantity =
                        remainingQuantity > 0
                            ? machine?.capacity *
                                  machine?.shifts?.[
                                      moment(date).format("YYYY-MM-DD")
                                  ]?.value -
                              toAllot
                            : quantity;
                    updatedDP = {
                        ...currentWO,
                        title: `${workOrder?.work_order?.work_order_code} - ${
                            newQuantity * (currentWO?.mould_size ?? 1)
                        }`,
                        target_original:
                            newQuantity * (currentWO?.mould_size ?? 1),
                        start: new Date(date),
                        end: new Date(date),
                        amount: newQuantity,
                    };
                    return updatedDP;
                }
                updatedDP = {
                    ...currentWO,
                    start: new Date(date),
                    end: new Date(date),
                };
                return updatedDP;
            }
            return event;
        });
        const updatedNewEvents = updatedEvents.filter((event) => {
            const eventDate = new Date(event.start).setHours(0, 0, 0, 0);
            const inputDate = new Date(date).setHours(0, 0, 0, 0);
            return eventDate === inputDate;
        });
        if (machine) {
            const checkQuentity = updatedNewEvents.reduce(
                (accumulator, current) => accumulator + current.amount,
                0
            );
            if (
                checkQuentity >
                machine?.capacity *
                    machine?.shifts?.[moment(date).format("YYYY-MM-DD")]?.value
            ) {
                Swal.fire({
                    icon: "error",
                    title: "Not allowed",
                    text: `Cannot assign quantity more than machine capacity i.e ${
                        machine?.capacity *
                        machine?.shifts?.[moment(date).format("YYYY-MM-DD")]
                            ?.value
                    }`,
                });
                return;
            }
        }
        if (!Array.isArray(additionalWOs)) {
            Swal.fire({
                icon: "error",
                title: "Not allowed",
                text: `Unable to find workorder`,
            });
            return;
        }
        if (updatedDP) {
            const allotted = additionalWOs.reduce(
                (accumulator, current) => accumulator + current.amount,
                0
            );
            if (remainingQuantity > allotted) {
                Swal.fire({
                    title: "Machine Capacity Issue",
                    text: `You are allocating the quantity ${
                        currentWO?.amount * (currentWO?.mould_size ?? 1)
                    }, but the machine capacity is not available to allocate all the quantity until the target date i.e ${moment(
                        workOrder?.work_order?.target_date
                    ).format(
                        "DD-MM-YYYY"
                    )}. Please allocate the remaining quantity i.e ${
                        (remainingQuantity - allotted) *
                        (currentWO?.mould_size ?? 1)
                    } to a different machine. Proceed with the given quantity ${
                        (machine?.capacity *
                            machine?.shifts?.[moment(date).format("YYYY-MM-DD")]
                                ?.value -
                            toAllot +
                            allotted) *
                        (currentWO?.mould_size ?? 1)
                    }?`,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Proceed",
                    cancelButtonText: "Cancel",
                }).then((result) => {
                    if (result.isConfirmed) {
                        handleUpdateDailyPlan(
                            updatedDP,
                            additionalWOs,
                            workOrder
                            // reason
                        );
                        setWoEvents([...updatedEvents, ...additionalWOs]);
                        return;
                    }
                });
            } else {
                handleUpdateDailyPlan(
                    updatedDP,
                    additionalWOs,
                    workOrder
                    // reason
                );
                setWoEvents([...updatedEvents, ...additionalWOs]);
            }
        }
    };

    const handleWORemove = () => {
        const updatedEvents = woEvents.filter((event) => {
            if (event.id === currentWO?.id) {
                return false;
            }
            return true;
        });
        deleteDailyPlan({
            id: currentWO?.id,
        });
        setWoEvents(updatedEvents);
    };

    return {
        machine,
        woEvents,
        handleSelectSlot,
        handleSelectEvent,
        dailyPlanData,
        currentSlot,
        currentWO,
        handleWOSelect,
        handleWOEdit,
        handleWORemove,
        setCurrentDate,
        loadingMachineInfoData,
        fetchingMachineData,
        creatingDailyPlans,
        deletingDailyPlan,
        errorLoadingWorkOrders,
        errorLoadingMachineInfo,
        loadingWorkOrdersData,
        fetchingDailyPlan,
    };
};

export default useDailyPlanCalendar;
